import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ScheduleMenu from '../../components/profile/MySchedules/ScheduleMenu';
import { Card, CardContent } from '../../components/ui/card';
import { useAlert } from '../../context/alert/AlertProvider';
import { useTranslation } from 'react-i18next';
import { useScenario } from '../../context/scenario/ScenarioContext';
import { Loader2, FileText, ArrowRight, Trash2, Calendar, Users, Filter, FilterX, LucideSidebar } from 'lucide-react';
import EditScenarioModal from '../../components/forms/scenario/EditScenarioModal';
import ConfirmDeleteModal from '../../components/forms/scenario/ConfirmDeleteModal';
import { minutesToTimeString } from '../../components/utils/cloneUtils';
import EditNameModal from '../../components/profile/MySchedules/EditSchedule/EditNameModal';

const ScenariosManagementPage = () => {
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [activeTab, setActiveTab] = useState('schedules');
  const [isLoading, setIsLoading] = useState(true);
  const [savedSchedules, setSavedSchedules] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [editScheduleModalOpen, setEditScheduleModalOpen] = useState(false);
  const [currentEditSchedule, setCurrentEditSchedule] = useState(null);
  const [showEditScenarioModal, setShowEditScenarioModal] = useState(false);
  const [showDeleteScenarioModal, setShowDeleteScenarioModal] = useState(false);
  const [teams, setTeams] = useState([]);
  const [selectedTeamFilter, setSelectedTeamFilter] = useState('all');
  const [showTeamFilter, setShowTeamFilter] = useState(false);
  const [loadingTeamData, setLoadingTeamData] = useState(false);
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const scenarioContext = useScenario();
  const location = useLocation();

  const fetchScenarios = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('https://www.ia-schedule.com:4139/api/scenario', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch scenarios');
      }

      const data = await response.json();
      setScenarios(data);

      if (data.length > 0) {
        setSelectedScenario(data[0]);
      }
    } catch (error) {
      console.error('Error fetching scenarios:', error);
      showAlert({
        title: t('alert.error'),
        description: t('alert.error.fetchScenarios'),
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [showAlert, t]);

  const fetchTeams = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://www.ia-schedule.com:4139/api/team', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch teams');
      }

      const data = await response.json();
      setTeams(data);
    } catch (error) {
      console.error('Error fetching teams:', error);
      showAlert({
        title: t('alert.error'),
        description: 'Erro ao carregar equipas',
        variant: 'destructive'
      });
    }
  }, [showAlert, t]);

  const fetchScenarioData = useCallback(async (scenarioId) => {
    try {
      setIsLoading(true);
      setLoadingTeamData(true);
      const token = localStorage.getItem('token');

      if (teams.length === 0) {
        return;
      }

      const schedulesResponse = await fetch(`https://www.ia-schedule.com:4139/api/roster/saved?scenarioId=${scenarioId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const draftsResponse = await fetch(`https://www.ia-schedule.com:4139/api/roster/drafts?scenarioId=${scenarioId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!schedulesResponse.ok) {
        throw new Error('Failed to fetch scenario data');
      }

      const schedulesData = await schedulesResponse.json();
      const draftsData = await draftsResponse.json();

      const schedulesWithTeams = await Promise.all(
        schedulesData.map(async (schedule) => {
          let teamId = null;

          try {
            const detailResponse = await fetch(`https://www.ia-schedule.com:4139/api/roster/saved/${schedule.id}`, {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              }
            });

            if (detailResponse.ok) {
              const detailData = await detailResponse.json();

              if (detailData.requestData && detailData.requestData.requestData) {
                const requestData = JSON.parse(detailData.requestData.requestData);

                if (requestData.Employees && Array.isArray(requestData.Employees)) {
                  const scheduleEmployeeNumbers = requestData.Employees
                    .map(emp => emp.Number)
                    .sort((a, b) => a - b);

                  for (const team of teams) {
                    if (team.employees && Array.isArray(team.employees)) {
                      const teamEmployeeNumbers = team.employees
                        .map(emp => emp.number)
                        .sort((a, b) => a - b);

                      if (scheduleEmployeeNumbers.length === teamEmployeeNumbers.length &&
                        scheduleEmployeeNumbers.every((num, index) => num === teamEmployeeNumbers[index])) {
                        teamId = team.id;
                        break;
                      }
                    }
                  }
                }
              }
            }
          } catch (error) {
            console.error(`Error processing schedule ${schedule.id}:`, error);
          }

          return {
            ...schedule,
            teamId
          };
        })
      );

      const draftsWithTeams = draftsData.map(draft => {
        let teamId = null;

        try {
          if (draft.data && draft.data.employees && Array.isArray(draft.data.employees)) {
            const draftEmployeeNumbers = draft.data.employees
              .map(emp => emp.number)
              .sort((a, b) => a - b);

            for (const team of teams) {
              if (team.employees && Array.isArray(team.employees)) {
                const teamEmployeeNumbers = team.employees
                  .map(emp => emp.number)
                  .sort((a, b) => a - b);

                if (draftEmployeeNumbers.length === teamEmployeeNumbers.length &&
                  draftEmployeeNumbers.every((num, index) => num === teamEmployeeNumbers[index])) {
                  teamId = team.id;
                  break;
                }
              }
            }
          }
        } catch (error) {
          console.error(`Error processing draft ${draft.id}:`, error);
        }

        return {
          ...draft,
          teamId
        };
      });

      setSavedSchedules(schedulesWithTeams);
      setDrafts(draftsWithTeams);

    } catch (error) {
      console.error('Error fetching scenario data:', error);
      showAlert({
        title: t('alert.error'),
        description: t('alert.error.fetchScenarioData'),
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
      setLoadingTeamData(false);
    }
  }, [showAlert, t, teams]);

  const getFilteredSchedules = (schedules) => {
    if (selectedTeamFilter === 'all') {
      return schedules;
    }

    return schedules.filter(schedule =>
      schedule.teamId && String(schedule.teamId) === String(selectedTeamFilter)
    );
  };

  useEffect(() => {
    const selectedScenarioId = location?.state?.selectedScenarioId;
    if (selectedScenarioId && scenarios.length > 0) {
      const scenarioToSelect = scenarios.find(s => s.id === selectedScenarioId);
      if (scenarioToSelect) {
        setSelectedScenario(scenarioToSelect);
      }
    }
  }, [scenarios, location]);

  useEffect(() => {
    fetchScenarios();
    fetchTeams();
  }, [fetchScenarios, fetchTeams]);

  useEffect(() => {
    if (selectedScenario && teams.length > 0) {
      fetchScenarioData(selectedScenario.id);
    }
  }, [selectedScenario, fetchScenarioData, teams]);

  const handleEditScheduleName = (schedule) => {
    setCurrentEditSchedule(schedule);
    setEditScheduleModalOpen(true);
  };

  const handleScenarioSelect = (scenarioId) => {
    const scenario = scenarios.find(s => s.id === parseInt(scenarioId));
    setSelectedScenario(scenario);
  };

  const handleEditScenario = () => {
    setShowEditScenarioModal(true);
  };

  const handleDeleteScenario = () => {
    setShowDeleteScenarioModal(true);
  };

  const hasActiveFilters = () => {
    return selectedTeamFilter !== 'all';
  };

  const clearAllFilters = () => {
    setSelectedTeamFilter('all');
  };

  const handleUpdateScenario = async (scenarioData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://www.ia-schedule.com:4139/api/scenario/${selectedScenario.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(scenarioData)
      });

      if (!response.ok) {
        throw new Error('Failed to update scenario');
      }

      const updatedScenarios = scenarios.map(s =>
        s.id === selectedScenario.id
          ? { ...s, ...scenarioData }
          : s
      );

      setScenarios(updatedScenarios);
      setSelectedScenario({ ...selectedScenario, ...scenarioData });
      setShowEditScenarioModal(false);

      showAlert({
        title: t('alert.success.updatedScenario'),
        description: t('scenarios.updated'),
        variant: 'success'
      });
    } catch (error) {
      console.error('Error updating scenario:', error);
      showAlert({
        title: t('alert.error.updateScenario'),
        description: t('alert.error.updateScenario'),
        variant: 'destructive'
      });
    }
  };

  const handleDeleteScenarioConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://www.ia-schedule.com:4139/api/scenario/${selectedScenario.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Detailed error:', errorData);
        throw new Error(errorData.message || 'Failed to delete scenario');
      }

      const updatedScenarios = scenarios.filter(s => s.id !== selectedScenario.id);
      setScenarios(updatedScenarios);
      setShowDeleteScenarioModal(false);

      if (updatedScenarios.length > 0) {
        setSelectedScenario(updatedScenarios[0]);
      } else {
        setSelectedScenario(null);
      }

      showAlert({
        title: t('alert.success.deleteScenario'),
        description: t('scenarios.deleted'),
        variant: 'success'
      });
    } catch (error) {
      console.error('Error deleting scenario:', error);
      showAlert({
        title: t('alert.error.deleteScenario'),
        description: t('alert.error.deleteScenario'),
        variant: 'destructive'
      });
    }
  };

  const handleViewSchedule = (scheduleId) => {
    navigate(`/schedules/${scheduleId}`);
  };

  const handleContinueDraft = (draft) => {
    try {
      const draftData = draft.data;

      sessionStorage.setItem('draftInfo', JSON.stringify({
        flag: 1,
      }));

      sessionStorage.setItem('scenarioId', JSON.stringify({
        scenarioId: draft.scenarioId
      }));

      const formattedEmployees = draftData.employees.map(emp => ({
        name: emp.name,
        wkl_wrk_load: emp.wkl_Wrk_Load,
        day_WI: emp.day_WI,
        at_roster_num: emp.at_Roster_Num,
        number: emp.number,
        team: emp.team
      }));

      const formattedGeneral = {
        week_or_days: draftData.generalInfo.week_or_days,
        weeks: draftData.generalInfo.weeks,
        days: draftData.generalInfo.days,
        cycle: draftData.generalInfo.cycle,
        offset: draftData.generalInfo.offset,
        GA: draftData.generalInfo.ga,
        OFFsum: draftData.generalInfo.offSum
      };

      const formattedSchedules = draftData.schedules.map(sch => ({
        code: sch.code,
        legend: sch.legend,
        description: sch.description,
        startDay: sch.startDay,
        endDay: sch.endDay,
        startTime: minutesToTimeString(sch.startTime),
        endTime: minutesToTimeString(sch.endTime),
        weightTime: sch.weightTime,
        schedule_type: sch.schedule_Type,
        mealBreaks: sch.mealBreaks.map(mb => ({
          enabled: mb.enabled,
          startTime: mb.startTime !== null ? minutesToTimeString(mb.startTime) : '',
          endTime: mb.endTime !== null ? minutesToTimeString(mb.endTime) : ''
        })),
        shiftTypes: sch.shiftTypes.map(st => ({
          active: st.active,
          type: st.type
        }))
      }));

      const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
      const formattedStaffRequirements = {
        max: Array(7).fill('0'),
        min: Array(7).fill('0')
      };

      draftData.staffRequirements.forEach(sr => {
        const dayIndex = daysOfWeek.indexOf(sr.dayOfWeek);
        if (dayIndex !== -1) {
          formattedStaffRequirements.max[dayIndex] = sr.maxStaff?.toString() || '0';
          formattedStaffRequirements.min[dayIndex] = sr.minStaff?.toString() || '0';
        }
      });

      const formattedDistributions = {
        Monday: [],
        Tuesday: [],
        Wednesday: [],
        Thursday: [],
        Friday: [],
        Saturday: [],
        Sunday: [],
        Holidays: [],
        "Eve of Holidays": []
      };

      draftData.staffDistributions.forEach(sd => {
        formattedDistributions[sd.dayOfWeek] = sd.timeIntervals.map(ti => ({
          startTime: ti.startTime,
          endTime: ti.endTime,
          staff: ti.staffCount.toString()
        }));
      });

      const formattedShiftSequences = draftData.shiftSequences.map(ss => ({
        shift: ss.shift,
        hard_min: ss.hard_Min,
        soft_min: ss.soft_Min,
        min_penalty: ss.min_Penalty,
        soft_max: ss.soft_Max,
        hard_max: ss.hard_Max,
        max_penalty: ss.max_Penalty
      }));

      const formattedWeeklySums = draftData.weeklySums.map(ws => ({
        shift: ws.shift,
        hard_min: ws.hard_Min,
        soft_min: ws.soft_Min,
        min_penalty: ws.min_Penalty,
        soft_max: ws.soft_Max,
        hard_max: ws.hard_Max,
        max_penalty: ws.max_Penalty
      }));

      const formattedShiftRestrictions = {};

      if (draftData.empOnlySch && Array.isArray(draftData.empOnlySch)) {
        draftData.empOnlySch.forEach(restriction => {
          if (Array.isArray(restriction) && restriction.length >= 2) {
            const employeeIndex = restriction[0];
            const shiftIndex = restriction[1];

            const employee = formattedEmployees[employeeIndex];
            if (employee) {
              const employeeId = employee.number.toString();
              const shiftCode = formattedSchedules[shiftIndex]?.code;

              if (shiftCode) {
                if (!formattedShiftRestrictions[employeeId]) {
                  formattedShiftRestrictions[employeeId] = [];
                }

                formattedShiftRestrictions[employeeId].push({
                  id: Date.now() + Math.random(),
                  scheduleCode: shiftCode,
                  allowOnly: true
                });
              }
            }
          }
        });
      }

      if (draftData.empForbidSch && Array.isArray(draftData.empForbidSch)) {
        draftData.empForbidSch.forEach(restriction => {
          if (Array.isArray(restriction) && restriction.length >= 2) {
            const employeeIndex = restriction[0];
            const shiftIndex = restriction[1];

            const employee = formattedEmployees[employeeIndex];
            if (employee) {
              const employeeId = employee.number.toString();
              const shiftCode = formattedSchedules[shiftIndex]?.code;

              if (shiftCode) {
                if (!formattedShiftRestrictions[employeeId]) {
                  formattedShiftRestrictions[employeeId] = [];
                }

                formattedShiftRestrictions[employeeId].push({
                  id: Date.now() + Math.random(),
                  scheduleCode: shiftCode,
                  allowOnly: false
                });
              }
            }
          }
        });
      }

      const formattedAbsences = {};

      if (draftData.absenceEntries && Array.isArray(draftData.absenceEntries)) {
        draftData.absenceEntries.forEach(absence => {
          if (Array.isArray(absence) && absence.length >= 3) {
            const employeeIndex = absence[0];
            const scheduleIndex = absence[1];
            const day = absence[2];

            if (!formattedAbsences[employeeIndex]) {
              formattedAbsences[employeeIndex] = {};
            }

            const absenceCode = formattedSchedules[scheduleIndex]?.code;
            if (absenceCode) {
              formattedAbsences[employeeIndex][day] = absenceCode;
            }
          }
        });
      }

      if (Object.keys(formattedAbsences).length > 0) {
        const absenceEntries = [];
        Object.entries(formattedAbsences).forEach(([employeeIndex, dayTypes]) => {
          Object.entries(dayTypes).forEach(([day, absenceCode]) => {
            const scheduleIndex = formattedSchedules.findIndex(s => s.code === absenceCode);
            if (scheduleIndex !== -1) {
              absenceEntries.push([parseInt(employeeIndex), scheduleIndex, parseInt(day)]);
            }
          });
        });

        sessionStorage.setItem('absenceEntries', JSON.stringify(absenceEntries));
      }

      const formattedLegislation = {
        restTime: draftData.legislation?.restTime ?? 11,
        consecutiveDays: draftData.legislation?.consecutiveDays ?? 5
      };

      const transitions = [];
      const transitions3 = [];
      const transitions4 = [];
      const transitions5 = [];
      const penalizedTransitionsShifts = [];

      if (draftData.penalizedTransitions && Array.isArray(draftData.penalizedTransitions)) {
        draftData.penalizedTransitions.forEach(transition => {
          if (Array.isArray(transition) && transition.length >= 2) {
            transitions.push([...transition]);

            const shiftIndices = transition.slice(0, -1);
            const shiftsSequence = shiftIndices.map(idx =>
              formattedSchedules[idx]?.code || 'Unknown'
            );

            penalizedTransitionsShifts.push({
              length: 2,
              sequence: shiftsSequence,
              indices: [...transition]
            });
          }
        });
      }

      if (draftData.penalizedTransitions3 && Array.isArray(draftData.penalizedTransitions3)) {
        draftData.penalizedTransitions3.forEach(transition => {
          if (Array.isArray(transition) && transition.length >= 3) {
            transitions3.push([...transition]);

            const shiftIndices = transition.slice(0, -1);
            const shiftsSequence = shiftIndices.map(idx =>
              formattedSchedules[idx]?.code || 'Unknown'
            );

            penalizedTransitionsShifts.push({
              length: 3,
              sequence: shiftsSequence,
              indices: [...transition]
            });
          }
        });
      }

      if (draftData.penalizedTransitions4 && Array.isArray(draftData.penalizedTransitions4)) {
        draftData.penalizedTransitions4.forEach(transition => {
          if (Array.isArray(transition) && transition.length >= 4) {
            transitions4.push([...transition]);

            const shiftIndices = transition.slice(0, -1);
            const shiftsSequence = shiftIndices.map(idx =>
              formattedSchedules[idx]?.code || 'Unknown'
            );

            penalizedTransitionsShifts.push({
              length: 4,
              sequence: shiftsSequence,
              indices: [...transition]
            });
          }
        });
      }

      if (draftData.penalizedTransitions5 && Array.isArray(draftData.penalizedTransitions5)) {
        draftData.penalizedTransitions5.forEach(transition => {
          if (Array.isArray(transition) && transition.length >= 5) {
            transitions5.push([...transition]);

            const shiftIndices = transition.slice(0, -1);
            const shiftsSequence = shiftIndices.map(idx =>
              formattedSchedules[idx]?.code || 'Unknown'
            );

            penalizedTransitionsShifts.push({
              length: 5,
              sequence: shiftsSequence,
              indices: [...transition]
            });
          }
        });
      }

      sessionStorage.setItem('employees', JSON.stringify(formattedEmployees));
      sessionStorage.setItem('general', JSON.stringify(formattedGeneral));
      sessionStorage.setItem('schedules', JSON.stringify(formattedSchedules));
      sessionStorage.setItem('absences', JSON.stringify(formattedAbsences));
      sessionStorage.setItem('legislation', JSON.stringify(formattedLegislation));
      sessionStorage.setItem('staffRequirements', JSON.stringify(formattedStaffRequirements));
      sessionStorage.setItem('distributions', JSON.stringify(formattedDistributions));
      sessionStorage.setItem('shiftSequenceParams', JSON.stringify(formattedShiftSequences));
      sessionStorage.setItem('weeklySumParams', JSON.stringify(formattedWeeklySums));
      sessionStorage.setItem('shiftRestrictions', JSON.stringify(formattedShiftRestrictions));
      sessionStorage.setItem('penalizedTransitions', JSON.stringify(transitions));
      sessionStorage.setItem('penalizedTransitions_3', JSON.stringify(transitions3));
      sessionStorage.setItem('penalizedTransitions_4', JSON.stringify(transitions4));
      sessionStorage.setItem('penalizedTransitions_5', JSON.stringify(transitions5));
      sessionStorage.setItem('penalizedTransitions_shifts', JSON.stringify(penalizedTransitionsShifts));

      navigate('/form/collaborators');
    } catch (error) {
      console.error('Error processing draft data:', error);
      showAlert({
        title: t("alert.error"),
        description: t("alert.error.loadDraft"),
        variant: 'destructive'
      });
    }
  };

  const handleDeleteDraft = async (draftId) => {
    if (!window.confirm(t("alert.confirmDeleteDraft"))) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://www.ia-schedule.com:4139/api/roster/drafts/${draftId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Detailed error:', errorData);
        throw new Error(errorData.message || 'Failed to delete scenario');
      }

      setDrafts(drafts.filter(d => d.id !== draftId));

      showAlert({
        title: t("alert.success.deleteDraft"),
        description: t("alert.success.deleteDraft"),
        variant: 'success'
      });
    } catch (error) {
      console.error('Error deleting draft:', error);
      showAlert({
        title: t("alert.error.deleteDraft"),
        description: t("alert.error.deleteDraft"),
        variant: 'destructive'
      });
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm("Are you sure you want to delete this schedule?")) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://www.ia-schedule.com:4139/api/roster/saved/${scheduleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete schedule');

      setSavedSchedules(savedSchedules.filter(s => s.id !== scheduleId));

      showAlert({
        title: t("alert.success"),
        description: t("alert.success.deleteSchedule"),
        variant: 'success'
      });
    } catch (error) {
      console.error("Error deleting schedule:", error);
      showAlert({
        title: t("alert.error"),
        description: t("alert.error.deleteSchedule"),
        variant: 'destructive'
      });
    }
  };

  const handleNewSchedule = () => {
    if (scenarioContext && scenarioContext.selectScenario) {
      scenarioContext.selectScenario(selectedScenario);
    }

    navigate('/form/collaborators', {
      state: { scenarioId: selectedScenario.id }
    });
  };

  const TeamFilter = ({ t }) => {
    return (
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">
            {t("filter.byTeamText")}:
          </span>
        </div>

        <select
          value={selectedTeamFilter}
          onChange={(e) => setSelectedTeamFilter(e.target.value)}
          className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="all">{t("filter.allTeams")}</option>
          {teams.map((team) => (
            <option key={team.id} value={team.id}>
              {team.name}
            </option>
          ))}
        </select>

        {selectedTeamFilter !== 'all' && (
          <button
            onClick={() => setSelectedTeamFilter('all')}
            className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 flex items-center gap-1"
          >
            <FilterX className="w-3 h-3" />
            {t("filter.cleanFilter")}
          </button>
        )}
      </div>
    );
  };

  const ScheduleCard = ({ item, type }) => {
    const isSchedule = type === 'schedules';
    const team = item.teamId ? teams.find(t => t.id === item.teamId) : null;

    return (
      <Card className={`hover:shadow-md transition-shadow border-l-4 ${isSchedule ? 'border-indigo-600' : 'border-amber-500'
        }`}>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center">
            <div className="flex-1 flex flex-col sm:flex-row justify-between sm:items-center">
              <div>
                <div className={`flex items-center gap-2 flex-wrap ${!isSchedule ? 'mb-1' : ''}`}>
                  {isSchedule && (
                    <ScheduleMenu
                      schedule={item}
                      onEdit={() => handleEditScheduleName(item)}
                      onDelete={() => handleDeleteSchedule(item.id)}
                      translations={t}
                    />
                  )}
                  <h3 className="text-lg font-semibold text-gray-900">
                    {item.name || (isSchedule ? 'Horário sem nome' : t('drafts.unnamed'))}
                  </h3>
                  {!isSchedule && (
                    <span className="text-xs font-normal text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                      {t('drafts.draft')}
                    </span>
                  )}
                </div>

                {team && (
                  <div className="flex items-center gap-1 text-sm text-indigo-600 mb-1">
                    <Users className="w-4 h-4" />
                    <span>{team.name}</span>
                  </div>
                )}

                <p className="text-sm text-gray-500">
                  {isSchedule ? t('schedules.savedOn') : t('drafts.savedOn')}{' '}
                  {new Date(isSchedule ? item.savedAt : item.createdAt).toLocaleDateString()}
                </p>
              </div>

              <div className="flex flex-row-reverse sm:flex-row items-center gap-3 mt-3 sm:mt-0">
                {isSchedule ? (
                  <button
                    onClick={() => handleViewSchedule(item.id)}
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
                  >
                    {t('schedules.viewDetails')}
                    <ArrowRight size={16} />
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => handleContinueDraft(item)}
                      className="px-4 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 flex items-center gap-1"
                    >
                      {t('drafts.continue')}
                      <ArrowRight size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteDraft(item.id)}
                      className="p-2 text-gray-400 hover:text-red-600 rounded-full hover:bg-gray-100"
                      title={t('drafts.delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (isLoading && !selectedScenario) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100 -mt-16 pt-16">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (scenarios.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 -mt-16 pt-16 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <Card>
            <CardContent className="p-8 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-900 mb-2">{t('scenarios.empty.title')}</h3>
              <p className="text-gray-600 mb-6">{t('scenarios.empty.description')}</p>
              <button
                onClick={() => navigate('/scenarios/manage')}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                {t('scenarios.create')}
              </button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 -mt-16 pt-16 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                {t('scenarios.management.title')}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {t('scenarios.management.subtitle')}
              </p>
            </div>

            {hasActiveFilters() && (
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded-md hover:bg-red-100"
              >
                <FilterX className="w-3 h-3" />
                <span>Limpar filtros</span>
              </button>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <select
              value={selectedScenario?.id || ''}
              onChange={(e) => handleScenarioSelect(e.target.value)}
              className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {scenarios.map((scenario) => (
                <option key={scenario.id} value={scenario.id}>
                  {scenario.name}
                </option>
              ))}
            </select>
            <button
              onClick={() => navigate('/scenarios/manage')}
              className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              {t('scenarios.management.manageScenarios')}
            </button>
          </div>
        </div>

        {selectedScenario && (
          <div className="mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                  <div>
                    <h2 className="text-xl font-semibold">
                      {selectedScenario.name}
                    </h2>
                    {selectedScenario.description && (
                      <p className="text-gray-600 mt-2">
                        {selectedScenario.description}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={handleEditScenario}
                      className="px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      {t('common.edit')}
                    </button>
                    <button
                      onClick={handleDeleteScenario}
                      className="px-3 py-1.5 border border-red-300 text-red-600 rounded-md hover:bg-red-50"
                    >
                      {t('common.delete')}
                    </button>
                    <button
                      onClick={handleNewSchedule}
                      className="px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                    >
                      {t('scenarios.management.newSchedule')}
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {selectedScenario && (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="mb-6 border-b border-gray-200 overflow-x-auto scrollbar-hide">
                <div className="flex space-x-6 whitespace-nowrap">
                  <button
                    onClick={() => setActiveTab('schedules')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'schedules'
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    {t('scenarios.management.tabs.schedules')}
                  </button>
                  <button
                    onClick={() => setActiveTab('drafts')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'drafts'
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    {t('scenarios.management.tabs.drafts')}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-700">{t("filter.visualization")}:</span>
                  <button
                    onClick={() => setShowTeamFilter(!showTeamFilter)}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${showTeamFilter
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    <Users className="w-4 h-4 inline mr-1" />
                    {t("filter.byTeam")}
                  </button>
                </div>
              </div>

              {showTeamFilter && <TeamFilter t={t} />}
            </div>

            {(isLoading || loadingTeamData) ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
              </div>
            ) : (
              <div className="space-y-4">
                {activeTab === 'schedules' && (
                  <>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        {t('scenarios.management.savedSchedules')}
                      </h3>
                    </div>

                    {savedSchedules.length === 0 ? (
                      <Card>
                        <CardContent className="p-6 text-center">
                          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900">
                            {t('scenarios.management.noSchedules')}
                          </h3>
                          <p className="text-gray-500 mt-1 mb-4">
                            {t('scenarios.management.noSchedules.description')}
                          </p>
                          <button
                            onClick={handleNewSchedule}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                          >
                            {t('scenarios.management.createFirstSchedule')}
                          </button>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="space-y-4">
                        {getFilteredSchedules(savedSchedules).length === 0 && selectedTeamFilter !== 'all' ? (
                          <Card>
                            <CardContent className="p-6 text-center">
                              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                              <h3 className="text-lg font-medium text-gray-900">
                                Nenhum horário encontrado
                              </h3>
                              <p className="text-gray-500 mt-1">
                                Não há horários para esta equipa
                              </p>
                            </CardContent>
                          </Card>
                        ) : (
                          getFilteredSchedules(savedSchedules).map((schedule) => (
                            <ScheduleCard
                              key={schedule.id}
                              item={schedule}
                              type="schedules"
                            />
                          ))
                        )}
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'drafts' && (
                  <>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        {t('scenarios.management.draftSchedules')}
                      </h3>
                    </div>

                    {drafts.length === 0 ? (
                      <Card>
                        <CardContent className="p-6 text-center">
                          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900">
                            {t('scenarios.management.noDrafts')}
                          </h3>
                          <p className="text-gray-500 mt-1 mb-4">
                            {t('scenarios.management.noDrafts.description')}
                          </p>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="space-y-4">
                        {getFilteredSchedules(drafts).length === 0 && selectedTeamFilter !== 'all' ? (
                          <Card>
                            <CardContent className="p-6 text-center">
                              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                              <h3 className="text-lg font-medium text-gray-900">
                                Nenhum rascunho encontrado
                              </h3>
                              <p className="text-gray-500 mt-1">
                                Não há rascunhos para esta equipa
                              </p>
                            </CardContent>
                          </Card>
                        ) : (
                          getFilteredSchedules(drafts).map((draft) => (
                            <ScheduleCard
                              key={draft.id}
                              item={draft}
                              type="drafts"
                            />
                          ))
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {editScheduleModalOpen && currentEditSchedule && (
        <EditNameModal
          isOpen={editScheduleModalOpen}
          onClose={() => setEditScheduleModalOpen(false)}
          onSave={async (newName) => {
            try {
              const token = localStorage.getItem('token');
              const response = await fetch(`https://www.ia-schedule.com:4139/api/roster/saved/${currentEditSchedule.id}/name`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ name: newName })
              });

              if (!response.ok) throw new Error('Failed to update schedule name');

              setSavedSchedules(savedSchedules.map(s =>
                s.id === currentEditSchedule.id
                  ? { ...s, name: newName }
                  : s
              ));

              setEditScheduleModalOpen(false);
              showAlert({
                title: t("alert.success"),
                description: t("alert.success.updateSchedule"),
                variant: 'success'
              });

              navigate('/myscenarios', {
                state: {
                  selectedScenarioId: selectedScenario.id
                }
              });
            } catch (error) {
              console.error('Error updating schedule name:', error);
              showAlert({
                title: t("alert.error"),
                description: t("alert.error.updateSchedule"),
                variant: 'destructive'
              });
            }
          }}
          initialName={currentEditSchedule.name}
          translations={t}
        />
      )}

      {showEditScenarioModal && (
        <EditScenarioModal
          isOpen={showEditScenarioModal}
          onClose={() => setShowEditScenarioModal(false)}
          onSave={handleUpdateScenario}
          scenario={selectedScenario}
        />
      )}

      {showDeleteScenarioModal && (
        <ConfirmDeleteModal
          isOpen={showDeleteScenarioModal}
          onClose={() => setShowDeleteScenarioModal(false)}
          onConfirm={handleDeleteScenarioConfirm}
          title={t('scenarios.delete.title')}
          message={t('scenarios.delete.message', { name: selectedScenario?.name })}
        />
      )}
    </div>
  );
};

export default ScenariosManagementPage;